import { useState } from "react";
import { ArrowUpRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, extractFormError } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function WaitlistForm({ defaultTier = "growth" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    tier: defaultTier,
    use_case: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/waitlist", form);
      track("Waitlist Submitted", { tier: form.tier });
      setDone(true);
      toast.success("You're on the waitlist. Welcome.");
    } catch (err) {
      toast.error(extractFormError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="waitlist-success"
        className="border border-gold/30 bg-white p-8 sm:p-12 text-center"
      >
        <div className="w-12 h-12 rounded-full border border-gold flex items-center justify-center mx-auto mb-6">
          <Check size={20} className="text-gold" />
        </div>
        <p className="overline">You&apos;re in</p>
        <h3 className="font-serif text-3xl sm:text-4xl mt-4 leading-tight">
          Welcome to the
          <br />
          <span className="italic text-aurora">GoldKey cohort.</span>
        </h3>
        <p className="text-neutral-500 mt-5 font-light max-w-md mx-auto leading-relaxed">
          Founding members get early access, lifetime credit pools and a
          90-day results guarantee. We&apos;ll be in touch when the door opens.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="waitlist-form"
      className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8"
    >
      <div>
        <label className="overline block mb-3" htmlFor="wl-name">Your name</label>
        <input
          id="wl-name"
          data-testid="waitlist-name"
          required
          value={form.name}
          onChange={update("name")}
          className="editorial-input"
          placeholder="Jane Doe"
        />
      </div>
      <div>
        <label className="overline block mb-3" htmlFor="wl-email">Work email</label>
        <input
          id="wl-email"
          data-testid="waitlist-email"
          required
          type="email"
          value={form.email}
          onChange={update("email")}
          className="editorial-input"
          placeholder="jane@company.com"
        />
      </div>
      <div>
        <label className="overline block mb-3" htmlFor="wl-company">Company</label>
        <input
          id="wl-company"
          data-testid="waitlist-company"
          value={form.company}
          onChange={update("company")}
          className="editorial-input"
          placeholder="Optional"
        />
      </div>
      <div>
        <label className="overline block mb-3" htmlFor="wl-role">Your role</label>
        <input
          id="wl-role"
          data-testid="waitlist-role"
          value={form.role}
          onChange={update("role")}
          className="editorial-input"
          placeholder="Founder, CMO, Head of Data…"
        />
      </div>
      <div className="md:col-span-2">
        <label className="overline block mb-3" htmlFor="wl-tier">Plan you&apos;d start on</label>
        <select
          id="wl-tier"
          data-testid="waitlist-tier"
          value={form.tier}
          onChange={update("tier")}
          className="editorial-input bg-white"
        >
          <option value="starter" className="bg-white">Starter - $49/mo</option>
          <option value="growth" className="bg-white">Growth - $199/mo</option>
          <option value="enterprise" className="bg-white">Enterprise - Custom</option>
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="overline block mb-3" htmlFor="wl-uc">What you&apos;d ship first</label>
        <textarea
          id="wl-uc"
          data-testid="waitlist-usecase"
          rows={4}
          value={form.use_case}
          onChange={update("use_case")}
          className="editorial-input resize-none"
          placeholder="One line on the dataset, model or campaign you'd run through GoldKey first."
        />
      </div>
      <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <p className="text-xs text-neutral-400 max-w-md">
          By joining the waitlist, you agree to receive a personal reply. We never share your details and never spam.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="waitlist-submit"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Sending…
            </>
          ) : (
            <>
              Join the waitlist <ArrowUpRight size={16} />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
